import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Bot,
  Sparkles,
  Brain,
  Zap,
  Shield,
  Star,
  type LucideIcon,
} from 'lucide-react';
import {
  PA_SKILL_MAX_ACTIVE_PER_TURN,
  PaJobSchema,
  PaJobStepStatusSchema,
  type ArtifactFormSubmission,
  type PaSkill,
  type PaSkillListResponse,
  type PaArtifact,
  type PaRoutine,
  type PaToolApproval,
  type WorkspaceState as PaWorkspaceState,
} from '@easyflow/shared';
import { useAuth } from '@/contexts/AuthContext';
import { fetchChatModelOptions } from '@/hooks/useChatModelOptions';
import { useToast } from '@/hooks/use-toast';
import { PERSONAL_ASSISTANT_UPDATED_EVENT, type PersonalAssistantUpdatedDetail, type PersonalAssistantSectionRequest } from '@/lib/personal-assistant-events';
import { apiClient } from '@/lib/api';
import { createPaStreamAccumulator, reducePaStreamEvent, type PaToolBadge, type PaRagMeta, type PaCitation } from './paStreamReducer';
import { appendRoutineChatDraft } from './workspace/routines/routineChatDraft';
import {
  WorkspaceShell,
  FloatingFab,
  usePaArtifacts,
  usePaAllArtifacts,
  usePaApprovals,
  usePaJobs,
  usePaWorkspaceState,
} from './workspace';
import { usePaAvatarPrefs } from './avatar/usePaAvatarPrefs';
import { AvatarSettingsDialog } from './avatar/AvatarSettingsDialog';
import type { LLMProviderExtended } from '@/constants/ai-providers';
import type { RoundEvent, WorkspaceUiArtifact } from './workspace/types';
import type { ChatModelOption } from './workspace';
import type { SlashCommandItem } from './workspace/SlashCommandMenu';
import type { InvokedSkillChip } from './workspace/ChatComposer';
import type { PaFaceState } from './avatar/PaFace';

interface PersonalAssistantChatProps {
  open: boolean;
  sectionRequest?: PersonalAssistantSectionRequest;
  onClose: () => void;
  assistantId: string;
  assistantName: string;
  assistantColor?: string;
  assistantIcon?: string;
  assistantDescription?: string | null;
  userFirstName?: string | null;
  onUnreadCountChange?: (count: number) => void;
}

interface Conversation {
  id: string;
  title?: string;
  created_at: string;
  updated_at: string;
  last_message_at?: string;
  message_count?: number;
  first_message_excerpt?: string | null;
  // T008 — override modello per-conversazione (null = default dell'agente).
  model_override?: string | null;
  // Task #3822 — conversazione dentro un Progetto del PA (null = chat libera).
  project_id?: string | null;
}

interface PersistedExecutedTool {
  name: string;
  status?: string;
  durationMs?: number;
  resultPreview?: string;
  args?: Record<string, unknown>;
  /** Fonti citate dal tool nativo (web search), persistite per il chip storico. */
  citations?: PaCitation[];
}

/**
 * Normalizza la colonna `tool_calls` persistita (oggetto `{ traces, executed,
 * rag }` lato workspace, oppure array legacy) nella lista di tool eseguiti usata
 * per i chip inline + ricostruzione timeline al reload. I metadati ricchi
 * (durata/anteprima/argomenti) sono ora persistiti col messaggio: il chip
 * storico è identico a quello live.
 */
function extractExecutedTools(toolCalls: unknown): PersistedExecutedTool[] | undefined {
  const isTool = (t: unknown): t is PersistedExecutedTool =>
    typeof t === 'object' && t !== null && typeof (t as { name?: unknown }).name === 'string';
  if (!toolCalls) return undefined;
  if (Array.isArray(toolCalls)) {
    const list = toolCalls.filter(isTool);
    return list.length > 0 ? list : undefined;
  }
  if (typeof toolCalls === 'object') {
    const executed = (toolCalls as { executed?: unknown }).executed;
    if (Array.isArray(executed)) {
      const list = executed.filter(isTool);
      return list.length > 0 ? list : undefined;
    }
  }
  return undefined;
}

/** Estrae la meta KB (`rag`) persistita nel `tool_calls` JSONB del turno. */
function extractPersistedRag(toolCalls: unknown): PaRagMeta | undefined {
  if (!toolCalls || typeof toolCalls !== 'object' || Array.isArray(toolCalls)) return undefined;
  const rag = (toolCalls as { rag?: unknown }).rag;
  if (typeof rag !== 'object' || rag === null) return undefined;
  const { chunks, latencyMs } = rag as { chunks?: unknown; latencyMs?: unknown };
  if (typeof chunks !== 'number' || chunks <= 0) return undefined;
  return { chunks, latencyMs: typeof latencyMs === 'number' ? latencyMs : 0 };
}

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  tool_calls?: PaToolBadge[];
  // Meta KB del turno: persistita nel `tool_calls` JSONB e rimappata al reload.
  rag?: PaRagMeta;
  created_at: string;
  rerank_scores?: number;
  rag_chunks_used?: number;
  // Task #1590 — filtri KB attivi al momento dell'invio del messaggio user.
  rag_filters?: { audience?: string[]; sensitivity?: string } | null;
  // Task #2327 — ragionamento dell'AI persistito col messaggio assistant.
  thinking?: string | null;
}

interface AttachedFile {
  id: string;
  file: File;
  url?: string;
  type: 'image' | 'document';
  preview?: string;
  // Task #1445 — stato esplicito invece di rimuovere silenziosamente i fallimenti
  status: 'uploading' | 'uploaded' | 'error';
  errorMessage?: string;
}

const MAX_UPLOAD_SIZE = 25 * 1024 * 1024;
const ALLOWED_UPLOAD_EXT = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'pdf', 'doc', 'docx', 'txt']);
function validateUploadFile(file: File): string | null {
  if (file.size > MAX_UPLOAD_SIZE) return 'File troppo grande (max 25 MB)';
  const name = file.name.toLowerCase();
  if (name.includes('..') || name.includes('/') || name.includes('\\')) return 'Nome file non valido';
  const ext = name.split('.').pop() || '';
  if (!ALLOWED_UPLOAD_EXT.has(ext)) return `Estensione .${ext} non supportata`;
  return null;
}

const ICON_MAP: Record<string, LucideIcon> = {
  bot: Bot,
  sparkles: Sparkles,
  brain: Brain,
  zap: Zap,
  shield: Shield,
  star: Star,
};

// T008 — solo questi provider espongono lo switcher modello (chat brain).
// I provider custom restano sul modello dell'agente (limite documentato).
const MODEL_SWITCH_PROVIDERS = new Set(['openai', 'claude', 'gemini']);

export default function PersonalAssistantChat({
  open,
  sectionRequest,
  onClose,
  assistantId,
  assistantName,
  assistantColor = '#7c3aed',
  assistantIcon,
  assistantDescription,
  userFirstName,
  onUnreadCountChange,
}: PersonalAssistantChatProps) {
  const { toast } = useToast();
  // Task #3822 — id utente corrente per la sezione Progetti (distingue le
  // conversazioni proprie da quelle degli altri membri del progetto).
  const { user: authUser } = useAuth();
  const currentUserId = authUser?.id ?? null;
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  // Task #2327 — ragionamento dell'AI accumulato durante lo streaming del turno.
  const [streamingThinking, setStreamingThinking] = useState('');
  const [activeTools, setActiveTools] = useState<PaToolBadge[]>([]);
  // Meta KB del turno in streaming (poi persistita e rimappata dallo storico).
  const [activeRag, setActiveRag] = useState<PaRagMeta | undefined>(undefined);
  // Task #2435 — messaggio di stato transitorio emesso dal backend (es. attesa
  // rate-limit, retry per sovraccarico). Mostrato come riga di feedback live,
  // azzerato a fine turno.
  const [streamingStatus, setStreamingStatus] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);
  // Task #3821 — skill disponibili (menu `/skill`) + richiami espliciti del
  // prossimo turno (GARANTITI attivi lato backend).
  const [availableSkills, setAvailableSkills] = useState<PaSkill[]>([]);
  const [invokedSkills, setInvokedSkills] = useState<InvokedSkillChip[]>([]);

  // Task #3821 — carica le skill attive (personali + condivise) per il menu
  // `/skill` del composer: la barra `/` è anche discovery delle procedure.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get<PaSkillListResponse>(
          `/personal-assistant/workspace/skills?assistantId=${encodeURIComponent(assistantId)}`,
        );
        if (!cancelled) setAvailableSkills(res.data?.skills ?? []);
      } catch (err) {
        console.warn('[PA] skills load failed', err);
      }
    })();
    return () => { cancelled = true; };
  }, [open, assistantId]);

  const slashSkills = useMemo<SlashCommandItem[]>(
    () => availableSkills
      .filter(s => s.status === 'active')
      .map(s => ({
        id: s.id,
        label: s.name,
        description: s.description,
        kind: 'skill' as const,
        ...(s.visibility === 'shared' ? { badge: 'Team' } : {}),
      })),
    [availableSkills],
  );

  const handleInvokeSkill = useCallback((item: SlashCommandItem) => {
    setInvokedSkills(prev => {
      if (prev.some(s => s.id === item.id)) return prev;
      // Cap CONDIVISO col backend (Zod .max sul body) e col motore di selezione:
      // il composer non accumula MAI più richiami di quanti il turno può
      // onorare — un chip mostrato è un chip garantito.
      if (prev.length >= PA_SKILL_MAX_ACTIVE_PER_TURN) {
        setStreamingStatus(`Massimo ${PA_SKILL_MAX_ACTIVE_PER_TURN} skill richiamabili per turno`);
        return prev;
      }
      return [...prev, { id: item.id, name: item.label }];
    });
  }, []);

  const handleRemoveInvokedSkill = useCallback((id: string) => {
    setInvokedSkills(prev => prev.filter(s => s.id !== id));
  }, []);
  // T008 — switcher modello header (opzione B): provider FISSO sull'agente, lo
  // switch è un override SOLO sul modello, scoped alla conversazione corrente.
  const [agentProvider, setAgentProvider] = useState<string | null>(null);
  const [agentDefaultModel, setAgentDefaultModel] = useState<string | null>(null);
  const [modelOptions, setModelOptions] = useState<ChatModelOption[]>([]);
  const [modelOverride, setModelOverride] = useState<string | null>(null);
  const modelSwitchEnabled = !!agentProvider && MODEL_SWITCH_PROVIDERS.has(agentProvider) && modelOptions.length > 0;
  const effectiveModelId = modelOverride ?? agentDefaultModel;
  const abortControllerRef = useRef<AbortController | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const unreadCountRef = useRef(0);
  const openRef = useRef(open);

  // Task #1400 — workspace state (artifacts, approvals, agent state, rounds)
  // Inline mini-card → scoped alla conversazione corrente (proprietari).
  const { artifacts: conversationArtifacts, upsert: upsertConvArtifact, refresh: refreshArtifacts, remove: removeConvArtifact } = usePaArtifacts(currentConversationId);
  // Pannello Artefatti → vista globale cross-conversazione (tutti).
  const { artifacts: allArtifacts, upsert: upsertAllArtifact, remove: removeAllArtifact } = usePaAllArtifacts();
  const { pending: pendingApprovals, add: addApproval, remove: removeApproval, decide: decideApprovalApi } = usePaApprovals(currentConversationId);
  const { state: workspaceState, setLocal: setWorkspaceStateLocal, refresh: refreshWorkspaceState, interrupt: interruptWorkspace, resume: resumeWorkspace } = usePaWorkspaceState(currentConversationId);
  const { conversationJobs, loading: jobsLoading, error: jobsError, refresh: refreshJobs, upsert: upsertJob, applyStep: applyJobStep, decide: decideJobApi, cancel: cancelJobApi } = usePaJobs(assistantId, currentConversationId, open && !isCollapsed);
  const [rounds, setRounds] = useState<RoundEvent[]>([]);
  const roundsRef = useRef<RoundEvent[]>([]);
  useEffect(() => { roundsRef.current = rounds; }, [rounds]);

  // Task #3041 — avatar personale del PA: prefs per-utente (flag + config +
  // nome), seed di default deterministico e stato della "faccia".
  const { user } = useAuth();
  const { prefs: avatarPrefs, save: saveAvatarPrefs } = usePaAvatarPrefs();
  const [avatarSettingsOpen, setAvatarSettingsOpen] = useState(false);
  const avatarDefaultSeed = useMemo(() => `pa-${assistantId}-${user?.id ?? 'me'}`, [assistantId, user?.id]);
  // Nome effettivo: il nome personale (se avatar attivo) subentra a quello dell'agente.
  const effectiveAssistantName = avatarPrefs.enabled && avatarPrefs.assistantName?.trim()
    ? avatarPrefs.assistantName
    : assistantName;
  // Esito transitorio del turno (completato/errore) mostrato ~2.5s sul layer di stato.
  const [turnFlash, setTurnFlash] = useState<'done' | 'error' | null>(null);
  const turnFlashTimerRef = useRef<number | null>(null);
  const flashTurnOutcome = useCallback((outcome: 'done' | 'error') => {
    if (turnFlashTimerRef.current != null) window.clearTimeout(turnFlashTimerRef.current);
    setTurnFlash(outcome);
    turnFlashTimerRef.current = window.setTimeout(() => setTurnFlash(null), 2500);
  }, []);
  useEffect(() => () => {
    if (turnFlashTimerRef.current != null) window.clearTimeout(turnFlashTimerRef.current);
  }, []);
  // Stato corrente del PA per il layer di stato dell'avatar, derivato dai
  // segnali già presenti nello stream del Workspace.
  const faceState: PaFaceState = useMemo(() => {
    if (pendingApprovals.length > 0 || workspaceState === 'awaiting_approval') return 'awaiting_approval';
    if (isStreaming) return activeTools.some(t => t.status === 'running') ? 'executing' : 'thinking';
    if (turnFlash) return turnFlash;
    return 'idle';
  }, [pendingApprovals.length, workspaceState, isStreaming, activeTools, turnFlash]);

  // Task #2365 — Stop reale desktop: aborta lo stream locale (fetch reader) e
  // segnala l'interruzione server-side, in modo che il turno corrente termini
  // subito senza essere scartato in silenzio.
  const handleStopStreaming = useCallback((): Promise<void> => {
    abortControllerRef.current?.abort();
    // Task #3820 — l'interrupt server-side annulla anche i lavori attivi: lo
    // stream è già abortito, quindi il job_updated non arriva → riallinea via GET.
    return interruptWorkspace()
      .then(() => refreshJobs())
      .catch(() => {});
  }, [interruptWorkspace, refreshJobs]);

  const handleDecideApproval = useCallback(async (
    id: string,
    decision: 'approved' | 'rejected',
    notes?: string,
    overrideArgs?: Record<string, unknown>,
  ) => {
    await decideApprovalApi(id, decision, notes, overrideArgs);
  }, [decideApprovalApi]);

  const handleArtifactAction = useCallback((artifact: WorkspaceUiArtifact, intent: string) => {
    const description = (artifact.content as { description?: unknown })?.description;
    const desc = typeof description === 'string' ? description : intent;
    setInputValue(prev => prev || `Esegui l'azione "${desc}" (slug ${artifact.slug}).`);
  }, []);

  useEffect(() => { openRef.current = open; }, [open]);

  const handleOpenRoutineInChat = useCallback((routine?: PaRoutine) => {
    setInputValue(draft => appendRoutineChatDraft(draft, routine));
  }, []);

  useEffect(() => {
    if (sectionRequest) setIsCollapsed(false);
  }, [sectionRequest]);

  const AssistantIcon = useMemo(() => {
    return ICON_MAP[assistantIcon || 'sparkles'] || Sparkles;
  }, [assistantIcon]);

  // T008 — carica provider/modello di default dell'agente + catalogo chat del
  // provider (solo openai/claude/gemini). Provider non supportati ⇒ switch off.
  // Estratto in callback così da poter essere rieseguito sia al mount/cambio
  // agente, sia all'apertura (safety-net), sia su evento di salvataggio config.
  // Concorrenza gestita da un token monotono: l'ultima invocazione vince, le
  // risposte stantie vengono scartate.
  const configLoadTokenRef = useRef(0);
  const loadAgentConfig = useCallback(async () => {
    if (!assistantId) return;
    const token = ++configLoadTokenRef.current;
    const isStale = () => token !== configLoadTokenRef.current;
    try {
      const agentRes = await apiClient.get<{ model?: string | null; llm_provider?: string | null }>(
        `/personal-assistant/agents/${assistantId}`,
      );
      if (isStale() || !agentRes.success || !agentRes.data) return;
      const provider = (agentRes.data.llm_provider || 'openai').toLowerCase();
      const defaultModel = agentRes.data.model ?? null;
      setAgentProvider(provider);
      setAgentDefaultModel(defaultModel);
      if (!MODEL_SWITCH_PROVIDERS.has(provider)) {
        // Provider senza switcher: niente catalogo e nessun override valido.
        setModelOptions([]);
        setModelOverride(null);
        return;
      }
      // Catalog v2 tipizzato (niente endpoint legacy /ai-tools/llm-models).
      const payload = await fetchChatModelOptions(provider as LLMProviderExtended);
      if (isStale()) return;
      const opts: ChatModelOption[] = payload.models
        .filter(m => m.status !== 'deprecated')
        .map(m => ({
          id: m.id,
          displayName: m.displayName,
          description: m.description,
          contextWindow: m.contextWindow,
          status: m.status,
        }));
      // Garantisce la presenza del default agente anche se assente dal catalogo.
      if (defaultModel && !opts.some(m => m.id === defaultModel)) {
        opts.unshift({ id: defaultModel, displayName: defaultModel });
      }
      setModelOptions(opts);
      // Riconcilia l'override per-conversazione col nuovo catalogo: se il modello
      // selezionato non è più disponibile, torna al default dell'agente (null).
      setModelOverride(prev => (prev && !opts.some(o => o.id === prev) ? null : prev));
    } catch (error) {
      if (!isStale()) console.error('[PersonalAssistantChat] Error loading model catalog:', error);
    }
  }, [assistantId]);

  useEffect(() => { void loadAgentConfig(); }, [loadAgentConfig]);

  // Refresh event-driven: il modale di configurazione agente emette l'evento al
  // salvataggio; rileggiamo provider/modello/catalogo senza ricaricare la pagina.
  useEffect(() => {
    const handler = (e: CustomEvent<PersonalAssistantUpdatedDetail>) => {
      const id = e.detail?.assistantId;
      if (id && id !== assistantId) return;
      void loadAgentConfig();
    };
    window.addEventListener(PERSONAL_ASSISTANT_UPDATED_EVENT, handler);
    return () => window.removeEventListener(PERSONAL_ASSISTANT_UPDATED_EVENT, handler);
  }, [assistantId, loadAgentConfig]);

  const loadConversations = useCallback(async () => {
    try {
      const res = await apiClient.get<Conversation[]>(`/personal-assistant/agents/${assistantId}/conversations`);
      if (res.success && res.data) {
        setConversations(Array.isArray(res.data) ? res.data : []);
      }
    } catch (error) {
      console.error('[PersonalAssistantChat] Error loading conversations:', error);
    }
  }, [assistantId]);

  const loadMessages = useCallback(async (convId: string) => {
    try {
      const res = await apiClient.get<{ messages?: unknown }>(`/personal-assistant/agents/${assistantId}/conversations/${convId}`);
      if (res.success && res.data) {
        const rawMsgs: unknown = res.data.messages;
        const list: Array<Omit<Message, 'tool_calls'> & { tool_calls?: unknown }> =
          Array.isArray(rawMsgs) ? rawMsgs : [];
        // Task #2367 — ricostruisci la timeline "Attività agente" dai tool
        // eseguiti persistiti, così sopravvive al reload/cambio conversazione.
        const reconstructed: RoundEvent[] = [];
        let roundNo = 0;
        const normalized: Message[] = list.map((m) => {
          const executed = extractExecutedTools(m.tool_calls);
          const rag = extractPersistedRag(m.tool_calls);
          if (executed && executed.length > 0) {
            roundNo += 1;
            reconstructed.push({
              id: `hist-${m.id ?? roundNo}`,
              round: roundNo,
              startedAt: m.created_at ?? new Date().toISOString(),
              completedAt: m.created_at ?? new Date().toISOString(),
              toolCalls: executed.map((e) => ({ name: e.name, status: e.status === 'error' ? 'error' : 'success' })),
            });
          }
          // Rimappa i tool eseguiti a chip ricchi (durata/anteprima/argomenti),
          // identici a quelli live, e aggancia la meta KB del turno.
          const toolBadges: PaToolBadge[] | undefined = executed?.map((e) => ({
            name: e.name,
            status: e.status,
            durationMs: e.durationMs,
            resultPreview: e.resultPreview,
            args: e.args,
            citations: e.citations,
          }));
          return { ...m, tool_calls: toolBadges, rag };
        });
        setMessages(normalized);
        setRounds(reconstructed);
        roundsRef.current = reconstructed;
      }
    } catch (error) {
      console.error('[PersonalAssistantChat] Error loading messages:', error);
    }
  }, [assistantId]);

  useEffect(() => {
    if (open) {
      loadConversations();
      // Safety-net: rilegge la config dell'agente all'apertura, nel caso un
      // evento di salvataggio sia stato perso (es. chat smontata al momento).
      void loadAgentConfig();
      unreadCountRef.current = 0;
      onUnreadCountChange?.(0);
    }
  }, [open, loadConversations, loadAgentConfig, onUnreadCountChange]);

  const createConversation = async (projectId?: string) => {
    try {
      const res = await apiClient.post<Conversation>(
        `/personal-assistant/agents/${assistantId}/conversations`,
        projectId ? { projectId } : undefined,
      );
      if (res.success && res.data) {
        const conv = res.data;
        setCurrentConversationId(conv.id);
        return conv.id;
      }
    } catch (error) {
      console.error('[PersonalAssistantChat] Error creating conversation:', error);
    }
    return null;
  };

  // Task #3822 — nuova chat dentro un progetto: crea la conversazione già
  // associata (eredita istruzioni/knowledge/skill del progetto) e la apre.
  const handleNewProjectConversation = async (projectId: string) => {
    const convId = await createConversation(projectId);
    if (!convId) throw new Error('Creazione conversazione nel progetto non riuscita');
    setMessages([]);
    setStreamingContent('');
    setModelOverride(null);
    await loadConversations();
  };

  const handleNewConversation = async () => {
    setCurrentConversationId(null);
    setMessages([]);
    setStreamingContent('');
    setModelOverride(null);
  };

  const handleDeleteConversation = async (convId: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    try {
      await apiClient.delete(`/personal-assistant/agents/${assistantId}/conversations/${convId}`);
      if (currentConversationId === convId) {
        setCurrentConversationId(null);
        setMessages([]);
      }
      await loadConversations();
    } catch (error) {
      console.error('[PersonalAssistantChat] Error deleting conversation:', error);
    }
  };

  const handleBulkDeleteConversations = async (ids: string[]): Promise<void> => {
    if (ids.length === 0) return;
    try {
      const res = await apiClient.post<{ deletedIds: string[]; deletedCount: number }>(
        `/personal-assistant/agents/${assistantId}/conversations/bulk-delete`,
        { ids },
      );
      const deletedIds = res.success && res.data ? res.data.deletedIds : ids;
      if (currentConversationId && deletedIds.includes(currentConversationId)) {
        setCurrentConversationId(null);
        setMessages([]);
      }
      await loadConversations();
    } catch (error) {
      console.error('[PersonalAssistantChat] Error bulk-deleting conversations:', error);
    }
  };

  const uploadFile = async (file: File): Promise<AttachedFile | null> => {
    // Task #1445 — validazione client-side mirror del backend (size + estensione + path traversal).
    const validationError = validateUploadFile(file);
    if (validationError) {
      toast({ title: 'Allegato rifiutato', description: `${file.name}: ${validationError}`, variant: 'destructive' });
      return null;
    }
    const isImage = file.type.startsWith('image/');
    const id = `file-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const af: AttachedFile = {
      id,
      file,
      type: isImage ? 'image' : 'document',
      preview: isImage ? URL.createObjectURL(file) : undefined,
      status: 'uploading',
    };
    setAttachedFiles(prev => [...prev, af]);

    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await apiClient.post<{ url: string }>('/personal-assistant/upload-file', formData);
      if (res?.success && res.data?.url) {
        const uploaded: AttachedFile = { ...af, url: res.data.url, status: 'uploaded' };
        setAttachedFiles(prev => prev.map(f => f.id === id ? uploaded : f));
        return uploaded;
      }
      throw new Error(res?.error || 'Upload fallito');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Upload fallito';
      console.error('[PersonalAssistantChat] Upload error:', err);
      // Task #1445 — NON rimuovere: marca come error per visibilità + toast.
      setAttachedFiles(prev => prev.map(f => f.id === id ? { ...f, status: 'error', errorMessage: msg } : f));
      toast({ title: 'Upload fallito', description: `${file.name}: ${msg}`, variant: 'destructive' });
      return null;
    }
  };

  const removeAttachedFile = (id: string) => {
    setAttachedFiles(prev => {
      const file = prev.find(f => f.id === id);
      if (file?.preview) URL.revokeObjectURL(file.preview);
      return prev.filter(f => f.id !== id);
    });
  };

  const recordingStartRef = useRef<number>(0);
  const toggleRecording = async () => {
    if (isRecording) {
      mediaRecorderRef.current?.stop();
      setIsRecording(false);
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : '';

      if (!mimeType) {
        console.error('[STT] No supported audio mimeType');
        return;
      }

      const recorder = new MediaRecorder(stream, { mimeType });
      audioChunksRef.current = [];
      recordingStartRef.current = Date.now();

      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };

      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        const durationMs = Math.max(0, Date.now() - recordingStartRef.current);
        if (audioBlob.size < 1000) return;

        try {
          const formData = new FormData();
          formData.append('audio', audioBlob, 'recording.webm');
          formData.append('duration', String(durationMs));
          // Task #1445 — STT dedicato workspace. Risposta tipizzata
          // WorkspaceSttResponse: { text, durationMs }.
          const res = await apiClient.post<{ text: string; durationMs: number }>(
            '/personal-assistant/workspace/stt',
            formData,
          );
          const text = res?.data?.text ?? '';
          if (res?.success && text) {
            setInputValue(prev => prev + (prev ? ' ' : '') + text);
          }
        } catch (err) {
          console.error('[STT] Transcription error:', err);
        }
      };

      recorder.start();
      mediaRecorderRef.current = recorder;
      setIsRecording(true);
    } catch (err) {
      console.error('[STT] Failed to start recording:', err);
      const e = err as { name?: string; message?: string };
      let description = e?.message || 'Impossibile avviare la registrazione';
      if (e?.name === 'NotAllowedError' || e?.name === 'PermissionDeniedError') {
        description = 'Permesso microfono negato. Abilita l\'accesso al microfono dalle impostazioni del browser.';
      } else if (e?.name === 'NotFoundError' || e?.name === 'DevicesNotFoundError') {
        description = 'Nessun microfono rilevato sul dispositivo.';
      }
      toast({ title: 'Microfono non disponibile', description, variant: 'destructive' });
    }
  };

  const sendMessage = async (messageText?: string, opts?: { formSubmission?: ArtifactFormSubmission }) => {
    const text = (messageText || inputValue).trim();
    const hasFiles = attachedFiles.some(f => f.url && f.status === 'uploaded');
    const hasInvokedSkills = invokedSkills.length > 0;
    const formSubmission = opts?.formSubmission;
    if ((!text && !hasFiles && !hasInvokedSkills && !formSubmission) || isStreaming) return;

    // Task #3821 — fallback `/skill nome` digitato a mano (senza pick dal
    // menu): risolve la skill per nome, la promuove a richiamo garantito e la
    // toglie dal testo inviato al modello.
    let outboundText = text;
    const turnInvoked = new Map<string, string>(invokedSkills.map(s => [s.id, s.name] as const));
    const slashMatch = outboundText.match(/^\/skill\s+([a-z0-9][a-z0-9_-]{1,63})\s*/i);
    if (slashMatch) {
      const wanted = slashMatch[1]!.toLowerCase();
      const found = availableSkills.find(s => s.status === 'active' && s.name === wanted);
      if (found) {
        turnInvoked.set(found.id, found.name);
        outboundText = outboundText.slice(slashMatch[0].length).trim();
      }
    }

    setInputValue('');
    const currentAttachments = [...attachedFiles].filter(f => f.url && f.status === 'uploaded');
    setAttachedFiles([]);

    let convId = currentConversationId;
    if (!convId) {
      convId = await createConversation();
      if (!convId) return;
    }

    const displayText = text
      || (formSubmission ? `📋 Form inviato: ${formSubmission.title || formSubmission.slug}` : `[${currentAttachments.length} allegat${currentAttachments.length === 1 ? 'o' : 'i'}]`);
    const userMessage: Message = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content: displayText + (text && currentAttachments.length > 0 ? ` [${currentAttachments.length} allegat${currentAttachments.length === 1 ? 'o' : 'i'}]` : ''),
      created_at: new Date().toISOString(),
    };
    setMessages(prev => [...prev, userMessage]);
    setIsStreaming(true);
    setStreamingContent('');
    setStreamingThinking('');
    setActiveTools([]);
    setActiveRag(undefined);
    setStreamingStatus('');

    // Prefisso tenant, Bearer e refresh single-flight sono di `apiClient.stream`.
    const url = `/personal-assistant/agents/${assistantId}/chat`;

    const controller = new AbortController();
    abortControllerRef.current = controller;

    // Timer di coalescing dello streaming (dichiarati fuori dal try così il
    // finally può cancellarli ed evitare flush "fantasma" dopo il reset).
    let contentTimer: number | null = null;
    let thinkingTimer: number | null = null;

    interface ChatBodyPayload {
      conversation_id: string;
      message: string;
      attachedFiles?: Array<{ url: string | undefined; name: string; type: AttachedFile['type']; mimetype: string }>;
      // T008 — override modello per-conversazione (== default ⇒ il backend azzera l'override).
      model_override?: string;
      // Task #3821 — skill richiamate esplicitamente: garantite attive nel turno.
      invoked_skill_ids?: string[];
      // Task #3817 — valori compilati di un artefatto form come input del turno.
      form_submission?: ArtifactFormSubmission;
    }
    const bodyPayload: ChatBodyPayload = {
      conversation_id: convId,
      message: outboundText
        || (formSubmission ? '' : (turnInvoked.size > 0 ? 'Procedi seguendo la skill richiamata.' : 'Analizza i file allegati')),
    };
    if (formSubmission) bodyPayload.form_submission = formSubmission;
    if (turnInvoked.size > 0) {
      bodyPayload.invoked_skill_ids = [...turnInvoked.keys()];
      setInvokedSkills([]); // i richiami valgono per UN turno
    }
    if (currentAttachments.length > 0) {
      bodyPayload.attachedFiles = currentAttachments.map(af => ({
        url: af.url,
        name: af.file.name,
        type: af.type,
        mimetype: af.file.type,
      }));
    }
    if (modelSwitchEnabled && effectiveModelId) {
      bodyPayload.model_override = effectiveModelId;
    }

    try {
      const response = await apiClient.stream(url, {
        method: 'POST',
        body: bodyPayload,
        signal: controller.signal,
      });

      const reader = response.body?.getReader();
      if (!reader) throw new Error('No reader available');

      const decoder = new TextDecoder();
      // Task #2344 — accumulo SSE (contenuto + ragionamento + tool) via reducer
      // puro: unica fonte di verità testabile in isolamento.
      let streamAcc = createPaStreamAccumulator();
      let buffer = '';

      // Coalescing degli update di rendering (~30fps): senza throttle ogni
      // token scatena un re-render + re-parse Markdown, rendendo lo streaming a
      // scatti "riga per riga". Accumuliamo nel reducer e flushiamo a intervalli
      // fissi; il flush finale è garantito da done/error/fallback + finally.
      const FLUSH_MS = 33;
      const scheduleContentFlush = () => {
        if (contentTimer != null) return;
        contentTimer = window.setTimeout(() => {
          contentTimer = null;
          setStreamingContent(streamAcc.content);
        }, FLUSH_MS);
      };
      const scheduleThinkingFlush = () => {
        if (thinkingTimer != null) return;
        thinkingTimer = window.setTimeout(() => {
          thinkingTimer = null;
          setStreamingThinking(streamAcc.thinking);
        }, FLUSH_MS);
      };
      const cancelFlushTimers = () => {
        if (contentTimer != null) { window.clearTimeout(contentTimer); contentTimer = null; }
        if (thinkingTimer != null) { window.clearTimeout(thinkingTimer); thinkingTimer = null; }
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const jsonStr = line.slice(6).trim();
          if (!jsonStr || jsonStr === '[DONE]') continue;

          try {
            const event = JSON.parse(jsonStr);

            if ((event.type === 'token' || event.type === 'content') && event.content) {
              streamAcc = reducePaStreamEvent(streamAcc, event);
              scheduleContentFlush();
            } else if (event.type === 'thinking' && event.content) {
              // Task #2327 — ragionamento incrementale dell'AI.
              streamAcc = reducePaStreamEvent(streamAcc, event);
              scheduleThinkingFlush();
            } else if (event.type === 'server_tool_use' && event.tool) {
              // Task #2327 — tool nativo lato provider (es. web_search Claude):
              // mostralo come badge attivo, marcato 'success' perché eseguito
              // dal provider stesso senza callback tool_result.
              streamAcc = reducePaStreamEvent(streamAcc, event);
              setActiveTools(streamAcc.activeTools);
            } else if (event.type === 'tool_call') {
              streamAcc = reducePaStreamEvent(streamAcc, event);
              setActiveTools(streamAcc.activeTools);
              setRounds(prev => {
                const last = prev[prev.length - 1];
                if (last && !last.completedAt) {
                  const updated: RoundEvent = { ...last, toolCalls: [...last.toolCalls, { name: event.tool, status: 'running' }] };
                  return [...prev.slice(0, -1), updated];
                }
                return [...prev, {
                  id: `r-${Date.now()}-${prev.length}`,
                  round: prev.length + 1,
                  startedAt: new Date().toISOString(),
                  toolCalls: [{ name: event.tool, status: 'running' }],
                }];
              });
            } else if (event.type === 'tool_result') {
              streamAcc = reducePaStreamEvent(streamAcc, event);
              setActiveTools(streamAcc.activeTools);
              setRounds(prev => {
                const last = prev[prev.length - 1];
                if (!last) return prev;
                const status: 'success' | 'error' = event.status === 'error' ? 'error' : 'success';
                const updated: RoundEvent = {
                  ...last,
                  toolCalls: last.toolCalls.map(tc => tc.name === event.tool && tc.status === 'running' ? { ...tc, status } : tc),
                };
                return [...prev.slice(0, -1), updated];
              });
            } else if (event.type === 'rag') {
              // Chunk KB recuperati: riga di stato live; la meta KB è anche
              // persistita (chiave `rag`) e rimappata identica al reload.
              streamAcc = reducePaStreamEvent(streamAcc, event);
              setActiveRag(streamAcc.rag);
            } else if (event.type === 'citation' && event.url) {
              // Fonte citata da un tool nativo (web search): agganciata al chip del
              // tool per l'espansione; persistita in `executed.citations` e rimappata
              // identica al reload.
              streamAcc = reducePaStreamEvent(streamAcc, event);
              setActiveTools(streamAcc.activeTools);
            } else if (event.type === 'conversation_titled' && event.conversation_id && event.title) {
              // Task #1445 — patch IN-PLACE della sidebar senza polling/refresh.
              const newTitle = String(event.title);
              const cid = String(event.conversation_id);
              setConversations(prev => prev.map(c => c.id === cid ? { ...c, title: newTitle } : c));
            } else if (event.type === 'state_changed') {
              const next = event.state as PaWorkspaceState | undefined;
              if (next) setWorkspaceStateLocal(next, typeof event.version === 'number' ? event.version : undefined);
            } else if (event.type === 'artifact_emitted' && event.artifact) {
              upsertConvArtifact(event.artifact as PaArtifact);
              upsertAllArtifact(event.artifact as PaArtifact);
            } else if ((event.type === 'job_proposed' || event.type === 'job_updated') && event.job) {
              upsertJob(PaJobSchema.parse(event.job));
            } else if (event.type === 'job_step' && event.job_id && event.step_id) {
              applyJobStep(String(event.job_id), String(event.step_id), PaJobStepStatusSchema.parse(event.status));
            } else if ((event.type === 'tool_approval_required' || event.type === 'approval_required') && event.approval) {
              addApproval(event.approval as PaToolApproval);
            } else if ((event.type === 'tool_approval_resolved' || event.type === 'approval_resolved') && event.approval_id) {
              removeApproval(String(event.approval_id));
            } else if (event.type === 'skill_activated' && typeof event.skill_name === 'string' && event.skill_name) {
              // Task #3821 — attivazione skill MAI invisibile: notice live nel
              // feed di stato (il modello la dichiara comunque in chat).
              const modeLabel = event.mode === 'auto' ? 'agganciata per pertinenza' : 'richiamata';
              setStreamingStatus(`Skill "${event.skill_name}" attiva (${modeLabel})`);
            } else if (event.type === 'skill_limit') {
              // Task #3821 — richiamo esplicito non onorato in pieno: il backend
              // lo riporta SEMPRE (mai scarto silenzioso), qui diventa visibile.
              const limitName = typeof event.skill_name === 'string' && event.skill_name ? `"${event.skill_name}"` : 'richiamata';
              const limitLabel = event.reason === 'truncated'
                ? `Skill ${limitName}: contenuto troncato per limite di spazio`
                : event.reason === 'over_cap'
                  ? `Skill ${limitName} non attivata: limite skill per turno raggiunto`
                  : `Skill ${limitName} non attivata: non più disponibile`;
              setStreamingStatus(limitLabel);
            } else if (event.type === 'status' && typeof event.message === 'string' && event.message) {
              // Stato transitorio leggibile dal backend (attesa rate-limit, retry
              // sovraccarico): instradato come ogni altro evento nell'accumulatore,
              // poi proiettato nel feed di stato unico.
              streamAcc = reducePaStreamEvent(streamAcc, event);
              setStreamingStatus(streamAcc.statusMessage ?? '');
            } else if (event.type === 'done') {
              // Task #2435 — i tool del messaggio finale derivano dall'accumulatore
              // locale (sempre aggiornato nel loop SSE), non da un ref che può
              // essere stale perché sincronizzato fuori dal loop di lettura.
              const toolCalls = streamAcc.activeTools.length > 0 ? [...streamAcc.activeTools] : undefined;
              if (streamAcc.content) {
                const assistantMsg: Message = {
                  id: `assistant-${Date.now()}`,
                  role: 'assistant',
                  content: streamAcc.content,
                  tool_calls: toolCalls,
                  // Meta KB del turno: in-memory ora, ricaricato dal DB al reload.
                  rag: streamAcc.rag,
                  created_at: new Date().toISOString(),
                  // Task #2327 — ragionamento dell'AI per questo turno (se presente).
                  thinking: streamAcc.thinking || null,
                };
                setMessages(prev => [...prev, assistantMsg]);

                if (!openRef.current) {
                  unreadCountRef.current += 1;
                  onUnreadCountChange?.(unreadCountRef.current);
                }
              }
              flashTurnOutcome('done');
              cancelFlushTimers();
              setStreamingContent('');
              setStreamingThinking('');
              setActiveTools([]);
              setActiveRag(undefined);
              setStreamingStatus('');
              streamAcc = createPaStreamAccumulator();
            } else if (event.type === 'error') {
              const errorMsg: Message = {
                id: `error-${Date.now()}`,
                role: 'assistant',
                content: `Errore: ${event.error || 'Si è verificato un errore'}`,
                created_at: new Date().toISOString(),
              };
              setMessages(prev => [...prev, errorMsg]);
              flashTurnOutcome('error');
              cancelFlushTimers();
              setStreamingContent('');
              setStreamingThinking('');
              setActiveTools([]);
              setActiveRag(undefined);
              setStreamingStatus('');
              streamAcc = createPaStreamAccumulator();
            }
          } catch {}
        }
      }

      cancelFlushTimers();
      if (streamAcc.content) {
        const fallbackMessage: Message = {
          id: `assistant-${Date.now()}`,
          role: 'assistant',
          content: streamAcc.content,
          created_at: new Date().toISOString(),
          thinking: streamAcc.thinking || null,
        };
        setMessages(prev => [...prev, fallbackMessage]);
        setStreamingContent('');
        setStreamingThinking('');
        if (!openRef.current) {
          unreadCountRef.current += 1;
          onUnreadCountChange?.(unreadCountRef.current);
        }
      }

      loadConversations().catch(() => {});
    } catch (error) {
      if ((error as { name?: unknown }).name !== 'AbortError') {
        console.error('[PersonalAssistantChat] Streaming error:', error);
        const errorMessage: Message = {
          id: `error-${Date.now()}`,
          role: 'assistant',
          content: 'Si è verificato un errore. Riprova.',
          created_at: new Date().toISOString(),
        };
        setMessages(prev => [...prev, errorMessage]);
        setStreamingContent('');
        flashTurnOutcome('error');
      }
    } finally {
      if (contentTimer != null) window.clearTimeout(contentTimer);
      if (thinkingTimer != null) window.clearTimeout(thinkingTimer);
      // Reset garantito dello stato live su OGNI uscita (inclusi AbortError e
      // throw imprevisti): evita UI "appesa" sui token già flushati senza
      // messaggio finale, che nascondeva la welcome a chat vuota.
      setStreamingContent('');
      setStreamingThinking('');
      setActiveTools([]);
      setActiveRag(undefined);
      setIsStreaming(false);
      setStreamingStatus('');
      abortControllerRef.current = null;
    }
  };

  // Task #3817 — i form artefatto inviano DAVVERO i valori compilati: partono
  // come `form_submission` del turno successivo (contratto Zod condiviso).
  // Definiti DOPO sendMessage (no-use-before-define).
  const handleArtifactFormSubmit = useCallback((_artifact: WorkspaceUiArtifact, submission: ArtifactFormSubmission) => {
    void sendMessage(undefined, { formSubmission: submission });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sendMessage è ridefinita a ogni render (function nel body): le dipendenze reali sono lette fresh alla chiamata
  }, []);

  // Task #3817 — rigenera: re-invia il testo dell'ultimo messaggio utente come
  // nuovo turno (nessun supporto regenerate lato backend; il suffisso allegati
  // "[N allegati]" mostrato in chat viene rimosso).
  const handleRegenerate = useCallback(() => {
    const lastUser = [...messages].reverse().find(m => m.role === 'user');
    if (!lastUser) return;
    const text = lastUser.content.replace(/\s*\[\d+ allegat[oi]\]\s*$/, '').trim();
    if (!text) return;
    void sendMessage(text);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sendMessage come sopra; messages letto fresh ad ogni render
  }, [messages]);

  if (!open && !isCollapsed) return null;

  if (!open) return null;

  if (isCollapsed) {
    return (
      <FloatingFab
        color={assistantColor}
        AssistantIcon={AssistantIcon}
        isStreaming={isStreaming}
        unreadCount={unreadCountRef.current}
        onExpand={() => setIsCollapsed(false)}
        avatarEnabled={avatarPrefs.enabled}
        avatarConfig={avatarPrefs.avatar}
        avatarDefaultSeed={avatarDefaultSeed}
        faceState={faceState}
      />
    );
  }

  return (
    <>
    <WorkspaceShell
      assistantId={assistantId}
      assistantName={effectiveAssistantName}
      assistantColor={assistantColor}
      assistantDescription={assistantDescription}
      userFirstName={userFirstName}
      AssistantIcon={AssistantIcon}
      conversations={conversations}
      currentConversationId={currentConversationId}
      onSelectConversation={(id) => {
        setCurrentConversationId(id);
        loadMessages(id);
        setModelOverride(conversations.find(c => c.id === id)?.model_override ?? null);
      }}
      onNewConversation={handleNewConversation}
      onDeleteConversation={(id) => { handleDeleteConversation(id); }}
      onBulkDeleteConversations={handleBulkDeleteConversations}
      messages={messages}
      streamingContent={streamingContent}
      isStreaming={isStreaming}
      streamingThinking={streamingThinking}
      activeTools={activeTools}
      activeRag={activeRag}
      streamingStatus={streamingStatus}
      inputValue={inputValue}
      onInputChange={setInputValue}
      onSendMessage={() => sendMessage()}
      workspaceState={workspaceState}
      rounds={rounds}
      artifacts={allArtifacts}
      conversationArtifacts={conversationArtifacts}
      approvals={pendingApprovals}
      onDecideApproval={handleDecideApproval}
      conversationJobs={conversationJobs}
      jobsLoading={jobsLoading}
      jobsError={jobsError}
      onRetryJobs={refreshJobs}
      onDecideJob={decideJobApi}
      onCancelJob={cancelJobApi}
      sectionRequest={sectionRequest}
      onOpenRoutineInChat={handleOpenRoutineInChat}
      onInterrupt={handleStopStreaming}
      onResume={resumeWorkspace}
      onArtifactAction={handleArtifactAction}
      onArtifactFormSubmit={handleArtifactFormSubmit}
      onRegenerate={handleRegenerate}
      onDeleteArtifact={(id) => { removeConvArtifact(id).then(() => removeAllArtifact(id)).catch(() => {}); }}
      modelOptions={modelOptions}
      defaultModelId={agentDefaultModel}
      currentModelId={effectiveModelId}
      modelSwitchEnabled={modelSwitchEnabled}
      onModelChange={(id) => setModelOverride(id === agentDefaultModel ? null : id)}
      attachedFiles={attachedFiles}
      onUploadFiles={(files) => { files.forEach((f) => { uploadFile(f).catch(() => {}); }); }}
      onRemoveFile={removeAttachedFile}
      isRecording={isRecording}
      onToggleRecording={() => { toggleRecording().catch(() => {}); }}
      currentUserId={currentUserId}
      onNewProjectConversation={handleNewProjectConversation}
      slashSkills={slashSkills}
      invokedSkills={invokedSkills}
      onInvokeSkill={handleInvokeSkill}
      onRemoveInvokedSkill={handleRemoveInvokedSkill}
      onCollapse={() => setIsCollapsed(true)}
      onClose={onClose}
      avatarEnabled={avatarPrefs.enabled}
      avatarConfig={avatarPrefs.avatar}
      avatarDefaultSeed={avatarDefaultSeed}
      faceState={faceState}
      onOpenAvatarSettings={() => setAvatarSettingsOpen(true)}
    />
    <AvatarSettingsDialog
      open={avatarSettingsOpen}
      onOpenChange={setAvatarSettingsOpen}
      prefs={avatarPrefs}
      defaultSeed={avatarDefaultSeed}
      assistantColor={assistantColor}
      FallbackIcon={AssistantIcon}
      baseAssistantName={assistantName}
      onSave={saveAvatarPrefs}
    />
    </>
  );
}
